"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { rem } from "polished";
import makeStyles from "../../theming/make-styles.js";
import { Backdrop } from "../backdrop/index.js";
import Portal from "../portal/index.js";
import { bounceIn, bounceOut } from "../utils/css/animation.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
import { KeyCodes } from "../utils/use-keyboard-navigation.js";
import useScrollLock from "../utils/use-scroll-lock.js";
const sizes = {
    small: rem(360),
    medium: rem(640),
    large: "none",
    fullsize: "none",
};
const useStyles = makeStyles()((theme, { color, size }) => {
    const palette = theme.palette(color).dialog;
    return {
        animateOut: {
            animation: `${bounceOut} .25s forwards`,
        },
        dialog: {
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            width: "100%",
            maxWidth: sizes[size],
            maxHeight: "100%",
            margin: size !== "fullsize" ? "auto" : undefined,
            backgroundColor: palette.dialog.backgroundColor,
            borderRadius: size !== "large" ? theme.size(4) : undefined,
            boxShadow: theme.shadow("sm"),
            pointerEvents: "auto",
            outline: 0,
            animation: `${bounceIn} .25s forwards`,
            [theme.breakpoints.up("md")]: size === "large" && {
                maxWidth: "50%",
            },
        },
        wrapper: {
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1001,
            display: "flex",
            pointerEvents: "none",
        },
    };
});
export const DialogContext = React.createContext({
    closable: true,
    size: "small",
});
export const Dialog = React.forwardRef(function Dialog(_a, ref) {
    var { children, className, closable = true, color = "primary", open = false, role = "dialog", size = "small", onClose, onOpen } = _a, props = __rest(_a, ["children", "className", "closable", "color", "open", "role", "size", "onClose", "onOpen"]);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [isMounted, setIsMounted] = useState(open);
    const { classes, cx } = useStyles({ color, size });
    const innerRef = useRef(ref);
    const combinedRef = useCombinedRefs(ref, innerRef);
    useScrollLock(open, combinedRef);
    useEffect(() => {
        if (open) {
            setIsMounted(true);
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        else if (isMounted) {
            setIsAnimatingOut(true);
        }
    }, [isMounted, onOpen, open]);
    useEffect(() => {
        var _a;
        (_a = combinedRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [combinedRef, isMounted]);
    const handleKeyDown = useCallback((e) => {
        if (e.keyCode === KeyCodes.Escape && closable && onClose) {
            e.stopPropagation();
            onClose === null || onClose === void 0 ? void 0 : onClose(e);
        }
    }, [closable, onClose]);
    const handleAnimationEnd = useCallback(() => {
        if (!open) {
            setIsMounted(false);
            setIsAnimatingOut(false);
        }
    }, [open]);
    return (isMounted && (_jsxs(_Fragment, { children: [_jsx(Backdrop, { transparent: false, onClick: (closable && onClose) || (() => { }) }), _jsx(Portal, { children: _jsx("div", Object.assign({ className: classes.wrapper, "data-component": "dialog" }, props, { children: _jsx("div", { ref: combinedRef, "aria-modal": "true", className: cx(classes.dialog, { [classes.animateOut]: isAnimatingOut }, className), role: role, tabIndex: 0, onAnimationEnd: handleAnimationEnd, onKeyDown: handleKeyDown, children: _jsx(DialogContext.Provider, { value: { closable, size, onClose }, children: children }) }) })) })] })));
});
export default Dialog;
//# sourceMappingURL=dialog.js.map